import * as React from "react";
import {
    Modal,
    Stack,
    Group,
    Text,
    Title,
    Loader,
    Center,
    Box,
    Divider,
    Code,
    ActionIcon,
    Tooltip,
    CopyButton,
    useMantineColorScheme,
    ThemeIcon as MantineThemeIcon,
    UnstyledButton,
} from "@mantine/core";
import { Database, Terminal, Copy, Check, Calendar, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { getReport, getChart, getDashboard, getForm, getTabGroup } from "@bi-studio/shared/apis";
import {
    ReportIcon,
    BarChartIcon,
    DashboardIcon,
    FormIcon,
    LineChartIcon,
    GaugeChartIcon,
    PieChartIcon,
    StackedLineChartIcon,
    BarLineChartIcon,
    RadarChartIcon,
    TabGroupIcon,
    ScoreCardIcon,
    AreaChartIcon,
    CalendarChartIcon,
    MapChartIcon,
    CorrelationChartIcon,
    ScatterChartIcon,
} from "@bi-studio/shared/components";

type ItemType = "report" | "chart" | "dashboard" | "form" | "tab_group";

interface ItemDetailModalProps {
    opened: boolean;
    onClose: () => void;
    itemType: ItemType | null;
    itemId: string | null;
}

const chartTypeLabels: Record<string, string> = {
    line: "Line Chart",
    bar: "Bar Chart",
    pie: "Pie Chart",
    gauge: "Gauge Chart",
    "stacked-line": "Stacked Line Chart",
    "stacked-bar-line": "Bar & Line Chart",
    radar: "Radar Chart",
    scorecard: "Scorecard",
    area: "Area Chart",
    calendar: "Calendar Chart",
    map: "Map Chart",
    scatter: "Scatter Chart",
    correlation: "Correlation Chart"
};

function Row({ label, value }: { label: string; value?: React.ReactNode | null }) {
    const { colorScheme } = useMantineColorScheme();
    if (!value) return null;
    return (
        <Group justify="space-between" align="flex-start" gap="xs">
            <Text size="sm" fw={500} c={colorScheme === 'light' ? 'gray.7' : 'dimmed'} w={110} style={{ flexShrink: 0 }}>
                {label}
            </Text>
            {typeof value === 'string' ? (
                <Text size="sm" style={{ wordBreak: 'break-word', flex: 1, textAlign: 'right' }}>{value}</Text>
            ) : (
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>{value}</div>
            )}
        </Group>
    );
}

export function ItemDetailModal({ opened, onClose, itemType, itemId }: ItemDetailModalProps) {
    const { token } = useAuth();
    const { colorScheme } = useMantineColorScheme();
    const navigate = useNavigate();
    const [data, setData] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!opened || !itemId || !itemType || !token) return;

        setData(null);
        setError(null);
        setLoading(true);

        const load = async () => {
            try {
                if (itemType === "report") {
                    setData(await getReport(token, itemId));
                } else if (itemType === "chart") {
                    setData(await getChart(token, itemId));
                } else if (itemType === "dashboard") {
                    setData(await getDashboard(token, itemId));
                } else if (itemType === "form") {
                    setData(await getForm(token, itemId));
                } else if (itemType === "tab_group") {
                    setData(await getTabGroup(token, itemId));
                }
            } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to load details.");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [opened, itemId, itemType, token]);

    const typeLabel: Record<ItemType, string> = {
        report: "Report",
        chart: "Chart",
        dashboard: "Dashboard",
        form: "Form",
        tab_group: "Tab Group",
    };



    const TypeIcon = () => {
        const iconSize = 24;
        if (itemType === "report") return <ReportIcon size={iconSize} />;
        if (itemType === "chart") {
            const chartType = data?.type;
            if (chartType === "line") return <LineChartIcon size={iconSize} />;
            if (chartType === "gauge") return <GaugeChartIcon size={iconSize} />;
            if (chartType === "pie") return <PieChartIcon size={iconSize} />;
            if (chartType === "stacked-line") return <StackedLineChartIcon size={iconSize} />;
            if (chartType === "stacked-bar-line") return <BarLineChartIcon size={iconSize} />;
            if (chartType === "radar") return <RadarChartIcon size={iconSize} />;
            if (chartType === "scorecard") return <ScoreCardIcon size={iconSize} />;
            if (chartType === "area") return <AreaChartIcon size={iconSize} />;
            if (chartType === "calendar") return <CalendarChartIcon size={iconSize} />;
            if (chartType === "map") return <MapChartIcon size={iconSize} />;
            if (chartType === "correlation") return <CorrelationChartIcon size={iconSize} />;
            if (chartType === "scatter") return <ScatterChartIcon size={iconSize} />;
            return <BarChartIcon size={iconSize} />;
        }
        if (itemType === "dashboard") return <DashboardIcon size={iconSize} />;
        if (itemType === "form") return <FormIcon size={iconSize} />;
        if (itemType === "tab_group") return <TabGroupIcon size={iconSize} />;
        return <Database size={18} />;
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={itemType ? `${typeLabel[itemType]} Details` : "Details"}
            size="md"
            centered
            overlayProps={{ backgroundOpacity: 0.1, blur: 2 }}
        >
            <Box
                p="md"
                style={{
                    border: '1px solid var(--modal-border)',
                    borderRadius: 'var(--mantine-radius-md)',
                    background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
                }}
            >
                {loading && (
                    <Center py="xl">
                        <Loader size="sm" />
                        <Text ml="sm" size="sm" c="dimmed">Loading details...</Text>
                    </Center>
                )}

                {error && (
                    <Center py="xl">
                        <Text c="red" size="sm">{error}</Text>
                    </Center>
                )}

                {!loading && !error && data && (
                    <Stack gap="md">
                        {/* Header */}
                        <Group gap="sm" align="center">
                            <TypeIcon />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Title order={4} style={{ lineHeight: 1.2 }}>{data.name || (data.config?.title)}</Title>
                            </div>
                        </Group>

                        <Divider />

                        {/* Common fields */}
                        <Stack gap="xs">
                            <Group justify="space-between" align="center" gap="xs">
                                <Text size="sm" fw={500} c={colorScheme === 'light' ? 'gray.7' : 'dimmed'} style={{ flexShrink: 0 }}>
                                    ID
                                </Text>
                                <Group gap={4} align="center" wrap="nowrap">
                                    <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>{data.id}</Text>
                                    <CopyButton value={data.id} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied!' : 'Copy ID'} withArrow>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color={copied ? 'teal' : 'gray'}
                                                    size="xs"
                                                    onClick={copy}
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    {copied ? <Check size={12} /> : <Copy size={12} />}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                            </Group>
                            {data.description && (
                                <>
                                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="xs">Description</Text>
                                    <Text size="sm" c={colorScheme === 'light' ? 'gray.7' : 'dimmed'}>
                                        {data.description}
                                    </Text>
                                </>
                            )}

                            {itemType === "chart" && data.type && (
                                <Row label="Chart Type" value={chartTypeLabels[data.type] || data.type} />
                            )}

                            <Group justify="space-between" align="center" gap="xs" mt="xs">
                                <Group gap={6}>
                                    <Calendar size={14} className="opacity-60" />
                                    <Text size="sm" fw={500} c={colorScheme === 'light' ? 'gray.7' : 'dimmed'}>
                                        Created At
                                    </Text>
                                </Group>
                                <Text size="sm">{dayjs(data.createdAt).format("DD MMM YYYY, HH:mm")}</Text>
                            </Group>
                        </Stack>

                        {/* Report-specific: linked data source */}
                        {itemType === "report" && data.dataSource && (
                            <Stack gap="xs">
                                <Divider />
                                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Data Source</Text>
                                <Box p="xs" style={{
                                    borderRadius: 'var(--mantine-radius-md)',
                                    border: '1px solid var(--modal-border)',
                                    backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)'
                                }}>
                                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                        <MantineThemeIcon variant="subtle" color="gray" size="sm">
                                            <Database size={14} />
                                        </MantineThemeIcon>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text size="sm" fw={500} className="truncate">{data.dataSource.name}</Text>
                                            <Text size="xs" c="dimmed" ff="monospace" className="truncate">{data.dataSource.id}</Text>
                                        </div>
                                    </Group>
                                </Box>
                            </Stack>
                        )}

                        {/* Report-specific: SQL query */}
                        {itemType === "report" && data.query && (
                            <Stack gap="xs">
                                <Group justify="space-between" align="center">
                                    <Group gap={6}>
                                        <Terminal size={14} />
                                        <Text size="sm" fw={600}>SQL Query</Text>
                                    </Group>
                                    <CopyButton value={data.query} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? 'Copied' : 'Copy SQL'} withArrow>
                                                <ActionIcon variant="light" color={copied ? 'teal' : 'gray'} size="xs" onClick={copy}>
                                                    {copied ? <Check size={12} /> : <Copy size={12} />}
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Group>
                                <Box
                                    p="sm"
                                    style={{
                                        border: '1px solid var(--modal-border)',
                                        borderRadius: 'var(--mantine-radius-md)',
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <Code block style={{ background: 'transparent', fontSize: 11 }}>
                                        {data.query}
                                    </Code>
                                </Box>
                            </Stack>
                        )}

                        {/* Chart-specific: linked report */}
                        {itemType === "chart" && data.report && (
                            <Stack gap="xs">
                                <Divider />
                                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Source Report</Text>
                                <UnstyledButton
                                    onClick={() => {
                                        onClose();
                                        navigate(`/reports/${data.report.id}`);
                                    }}
                                    p="xs"
                                    style={{
                                        borderRadius: 'var(--mantine-radius-md)',
                                        border: '1px solid var(--modal-border)',
                                        backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)'
                                    }}
                                    className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                >
                                    <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                        <MantineThemeIcon variant="subtle" color="gray" size="sm">
                                            <ReportIcon size={14} />
                                        </MantineThemeIcon>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text size="sm" fw={500} className="truncate">{data.report.name}</Text>
                                            <Text size="xs" c="dimmed" ff="monospace" className="truncate">{data.report.id}</Text>
                                        </div>
                                        <ChevronRight size={14} className="opacity-40" />
                                    </Group>
                                </UnstyledButton>
                            </Stack>
                        )}

                        {/* Dashboard-specific: items list */}
                        {itemType === "dashboard" && data.items && data.items.length > 0 && (
                            <Stack gap="xs">
                                <Divider />
                                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Dashboard Items</Text>
                                <Stack gap={4}>
                                    {data.items.slice(0, 5).map((item: any) => {
                                        const itemName = item.report?.name || item.chart?.config?.title || "Untitled";
                                        const itemIcon = item.report ? <ReportIcon size={14} /> : (item.chart ? <BarChartIcon size={14} /> : null);
                                        const type = item.report ? 'report' : 'chart';
                                        const id = item.reportId || item.chartId;

                                        return (
                                            <UnstyledButton
                                                key={item.id}
                                                onClick={() => {
                                                    onClose();
                                                    navigate(`/${type}s/${id}`);
                                                }}
                                                p="xs"
                                                style={{
                                                    borderRadius: 'var(--mantine-radius-sm)',
                                                    border: '1px solid var(--modal-border)',
                                                    backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.01)'
                                                }}
                                                className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                            >
                                                <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                                    <MantineThemeIcon variant="subtle" color="gray" size="xs">
                                                        {itemIcon}
                                                    </MantineThemeIcon>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <Text size="xs" fw={500} className="truncate">{itemName}</Text>
                                                    </div>
                                                    <ChevronRight size={12} className="opacity-40" />
                                                </Group>
                                            </UnstyledButton>
                                        );
                                    })}
                                    {data.items.length > 5 && (
                                        <Text size="xs" c="dimmed" ta="center">And {data.items.length - 5} more items...</Text>
                                    )}
                                </Stack>
                            </Stack>
                        )}

                        {/* Form-specific: field count */}
                        {itemType === "form" && (
                            <Row label="Fields" value={data.fields?.length != null ? `${data.fields.length} field(s)` : undefined} />
                        )}

                        {/* Tab Group-specific: tabs list */}
                        {itemType === "tab_group" && data.tabs && data.tabs.length > 0 && (
                            <Stack gap="xs">
                                <Divider />
                                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Tabs</Text>
                                <Stack gap={4}>
                                    {data.tabs.map((tab: any, index: number) => {
                                        const tabIcon = tab.type === 'report' ? <ReportIcon size={14} /> : (tab.type === 'chart' ? <BarChartIcon size={14} /> : <DashboardIcon size={14} />);
                                        const typeId = tab.reportId || tab.chartId || tab.dashboardId;

                                        return (
                                            <UnstyledButton
                                                key={index}
                                                onClick={() => {
                                                    onClose();
                                                    navigate(`/${tab.type}s/${typeId}`);
                                                }}
                                                p="xs"
                                                style={{
                                                    borderRadius: 'var(--mantine-radius-sm)',
                                                    border: '1px solid var(--modal-border)',
                                                    backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.01)'
                                                }}
                                                className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                            >
                                                <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                                    <MantineThemeIcon variant="subtle" color="gray" size="xs">
                                                        {tabIcon}
                                                    </MantineThemeIcon>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <Text size="xs" fw={500} className="truncate">{tab.title || "Untitled Tab"}</Text>
                                                        <Text size="xs" c="dimmed" className="truncate">{tab.type.charAt(0).toUpperCase() + tab.type.slice(1)}</Text>
                                                    </div>
                                                    <ChevronRight size={12} className="opacity-40" />
                                                </Group>
                                            </UnstyledButton>
                                        );
                                    })}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Modal>
    );
}
